import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import './App.css';

const API_BASE = 'https://api.highpriest.net';
const CHAT_ENDPOINT = `${API_BASE}/api/chat`;
const BLESSING_ENDPOINT = `${API_BASE}/api/blessing`;

const Chatbot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [blessingLoading, setBlessingLoading] = useState(false);

  const canSend = useMemo(() => !sending && input.trim() !== '', [sending, input]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => alert('Copied to clipboard!'),
      (err) => console.error('Failed to copy: ', err)
    );
  };

  const appendMessage = (message) => {
    setMessages((prev) => [...prev, message]);
  };

  const sendMessage = async () => {
    if (!canSend) return;

    const userText = input.trim();
    appendMessage({ text: userText, sender: 'user', ts: Date.now() });
    setInput('');

    try {
      setSending(true);
      const response = await axios.post(CHAT_ENDPOINT, {
        message: userText,
      });

      const botText = response?.data?.reply || 'I did not receive a response.';
      appendMessage({ text: botText, sender: 'bot', ts: Date.now() });
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error details:', error.response?.data || error.message);
      appendMessage({
        text: 'There was an error sending your message. Please try again in a moment.',
        sender: 'bot',
        ts: Date.now(),
      });
    } finally {
      setSending(false);
    }
  };

  const requestBlessing = async () => {
    if (blessingLoading) return;

    appendMessage({ text: 'Blessing', sender: 'user', ts: Date.now(), kind: 'action' });

    try {
      setBlessingLoading(true);
      const response = await axios.post(BLESSING_ENDPOINT, {});
      const blessingText = response?.data?.blessing;

      if (!blessingText) {
        appendMessage({
          text: 'I could not produce a blessing at this time. Please try again later.',
          sender: 'bot',
          ts: Date.now(),
        });
        return;
      }

      appendMessage({
        text: blessingText,
        sender: 'bot',
        ts: Date.now(),
        kind: 'blessing',
        source: response?.data?.source || null,
      });
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      console.error('Error requesting blessing:', error);
      console.error('Error details:', data || error.message);

      if (status === 429) {
        const nextAllowedAt = data?.nextAllowedAt;
        const humanTime = nextAllowedAt ? new Date(nextAllowedAt).toLocaleString() : null;
        appendMessage({
          text: humanTime
            ? `You have already received your blessing for today. Return after: ${humanTime}`
            : 'You have already received your blessing for today. Please return tomorrow.',
          sender: 'bot',
          ts: Date.now(),
        });
      } else {
        appendMessage({
          text: 'There was an error generating a blessing. Please try again later.',
          sender: 'bot',
          ts: Date.now(),
        });
      }
    } finally {
      setBlessingLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      sendMessage();
    }
  };

  return (
    <div className="chatbot">
      <h2>Ask Aaron</h2>
      <div className="chat-window">
        {messages.map((message, index) => (
          <div key={index} className={`message ${message.sender}`}>
            <div className="message-text">{message.text}</div>
            {message.sender === 'bot' && (
              <FontAwesomeIcon
                icon={faCopy}
                onClick={() => copyToClipboard(message.text)}
                className="copy-icon"
                title="Copy to clipboard"
              />
            )}
            {message.sender === 'bot' && message.kind === 'blessing' && message.source ? (
              <div className="blessing-source">Source: {message.source}</div>
            ) : null}
          </div>
        ))}
      </div>

      <input
        id="chatbot-input"
        name="chatbot-input"
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder="Type your message..."
        disabled={sending}
      />

      <div className="chatbot-actions">
        <button onClick={sendMessage} disabled={!canSend}>
          {sending ? 'Sending...' : 'Send'}
        </button>
        <button onClick={requestBlessing} disabled={blessingLoading}>
          {blessingLoading ? 'Blessing...' : 'Blessing'}
        </button>
      </div>
    </div>
  );
};

export default Chatbot;